import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosConfig from "../config/axios";
import {
  getPredefinedRoutes,
  getPredefinedRouteById,
  createPredefinedRoute,
  updatePredefinedRoute,
  deletePredefinedRoute,
} from "../services/predefinedRoutes";

export const fetchPredefinedRoutes = async ({ queryKey }) => {
  const [_key, searchTitle, searchCategory, pageNumber, itemsPerPage] =
    queryKey;

  try {
    const params = {
      pageNumber,
      pageSize: itemsPerPage,
      unionColumns: "TitleEng,TitleLat,TitleCir",
    };

    if (searchTitle?.trim()) {
      params.title = searchTitle.trim();
    }

    if (searchCategory !== "") {
      params.category = searchCategory;
    }

    const data = await getPredefinedRoutes(params);
    return data;
  } catch (error) {
    console.error("Error while fetching predefined routes", error);
  }
};

export const useFetchPredefinedRoutesData = (
  searchTitle,
  searchCategory,
  pageNumber,
  itemsPerPage,
) => {
  const {
    data: predefinedRoutes,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    [
      "predefined-routes",
      searchTitle,
      searchCategory,
      pageNumber,
      itemsPerPage,
    ],
    fetchPredefinedRoutes,
    {
      cacheTime: 3600000,
      keepPreviousData: true,
    },
  );

  return { predefinedRoutes, isLoading, isError, refetch };
};

export const useFetchPredefinedRouteById = (id) => {
  const {
    data: predefinedRoute,
    isLoading,
    isError,
    refetch,
  } = useQuery(["predefined-route", id], () => getPredefinedRouteById(id), {
    enabled: !!id,
    cacheTime: 3600000,
  });

  return { predefinedRoute, isLoading, isError, refetch };
};

export const useCreatePredefinedRoute = () => {
  const queryClient = useQueryClient();

  return useMutation(createPredefinedRoute, {
    onSuccess: () => {
      queryClient.invalidateQueries("predefined-routes");
    },
  });
};

export const useUpdatePredefinedRoute = () => {
  const queryClient = useQueryClient();

  return useMutation(({ id, payload }) => updatePredefinedRoute(id, payload), {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries("predefined-routes");
      queryClient.invalidateQueries(["predefined-route", variables.id]);
    },
  });
};

export const useRemovePredefinedRoute = () => {
  const queryClient = useQueryClient();

  return useMutation(deletePredefinedRoute, {
    onSuccess: () => {
      queryClient.invalidateQueries("predefined-routes");
    },
  });
};

export const searchLocationsForWaypoints = async (searchValue) => {
  const trimmedValue = searchValue?.trim() || "";

  if (trimmedValue.length < 3) {
    return { locations: [] };
  }

  const params = {
    nameEnglish: trimmedValue,
    nameLatin: trimmedValue,
    nameCyrillic: trimmedValue,
    pageNumber: 1,
    pageSize: 20000,
    intersectionColumns: "Categories",
    unionColumns: "NameEnglish,NameLatin,NameCyrillic",
  };

  const { data } = await axiosConfig.get("/admin/locations", {
    params,
  });

  const sortedLocations = [...(data?.locations || [])].sort(
    (firstLocation, secondLocation) => {
      const firstName =
        firstLocation.nameLat ||
        firstLocation.nameEng ||
        firstLocation.nameCir ||
        "";

      const secondName =
        secondLocation.nameLat ||
        secondLocation.nameEng ||
        secondLocation.nameCir ||
        "";

      return firstName.length - secondName.length;
    },
  );

  return {
    ...data,
    locations: sortedLocations,
  };
};

export const useSearchLocationsForWaypoints = (searchValue) => {
  const trimmedValue = searchValue?.trim() || "";

  const {
    data: locationSuggestions,
    isLoading,
    isError,
  } = useQuery(
    ["route-waypoint-locations", trimmedValue],
    () => searchLocationsForWaypoints(trimmedValue),
    {
      enabled: trimmedValue.length >= 3,
      keepPreviousData: true,
    },
  );

  return { locationSuggestions, isLoading, isError };
};
